import {useCallback, useMemo} from 'react';

import {AggPolicyStatus, PolicyStatusType} from 'modules/decisionsNext/api/types';

import {
  amsPolicyStatuses,
  extendedNonAmsPolicyStatuses,
  lostPolicyStatuses,
  policyStatusLabelsMap,
} from 'modules/decisionsNext/constants/policyStatus';
import {useAmsPolicyStatusCodesTreeQuery} from 'modules/decisionsNext/api/trees/amsPolicyStatusCodesTree.generated';
import {groupSelectedKeys, groupSelectedKeysByLevel, ungroupSelectedKeys, iterateTree} from 'utils/treeUtils';

import {useOptimizedSelection} from 'components/Tree/hooks/useOptimizedSelection';
import {SkipQueryParams, StoreAdapterParams, TreeBasicNode} from 'components/Tree/TreeTypes';

export function usePolicyStatusesTree(policyStatusType?: PolicyStatusType | null, skip?: boolean) {
  const {loading, data} = useAmsPolicyStatusCodesTreeQuery({
    skip: skip || policyStatusType !== PolicyStatusType.Ams,
    disableSkipSetsLoadingState: true,
  });

  const amsPolicyStatusCodesMap = useMemo<Record<AggPolicyStatus, string[]> | undefined>(() => {
    return data?.amsPolicyStatuses?.reduce((acc, status) => {
      acc[status.aggStatus] = status.codes;
      return acc;
    }, {} as Record<AggPolicyStatus, string[]>);
  }, [data]);

  const treeData = useMemo(() => {
    if (policyStatusType === PolicyStatusType.Ams) {
      return amsPolicyStatuses.map(status => ({
        key: status,
        title: policyStatusLabelsMap[status],
        children: (amsPolicyStatusCodesMap?.[status] || []).map(code => ({
          key: code,
          title: code,
        })),
      }));
    }

    return [...extendedNonAmsPolicyStatuses, ...lostPolicyStatuses].map(status => ({
      key: status,
      title: policyStatusLabelsMap[status],
    }));
  }, [policyStatusType, amsPolicyStatusCodesMap]);

  return {
    treeData,
    loading,
  };
}

export const useFullPolicyStatusTree = (skip?: boolean) => {
  const {treeData: ennablStatuses} = usePolicyStatusesTree(PolicyStatusType.Ennabl, skip);
  const {treeData: amsStatuses} = usePolicyStatusesTree(PolicyStatusType.Ams, skip);

  return useMemo(() => {
    const treeData = [...ennablStatuses, ...amsStatuses];
    const treeMap = new Map<string, TreeBasicNode>();

    iterateTree(
      treeData,
      (node, parent) => {
        treeMap.set(node.key, {key: node.key, title: node.title, parentKey: parent?.key});
      },
      {
        withParent: true,
      }
    );

    return {
      treeData,
      treeMap,
      loading: false,
    };
  }, [ennablStatuses, amsStatuses]);
};

export const usePolicyStatusesSelection = ({
  setStore,
  store,
  skip,
}: StoreAdapterParams<'policyStatusesAnd' | 'policyStatusType'> & SkipQueryParams) => {
  const {policyStatusesAnd, policyStatusType} = store;
  const {treeData, loading} = usePolicyStatusesTree(policyStatusType, skip);

  const selected = useMemo(() => {
    const policyStatusFilter = policyStatusesAnd?.[0];
    const allIds = [...(policyStatusFilter?.statuses || []), ...(policyStatusFilter?.codes || [])];

    return Array.from(ungroupSelectedKeys(treeData, allIds));
  }, [policyStatusesAnd, treeData]);

  const setSelected = useCallback(
    (newSelected: string[]) => {
      const groupedKeys = groupSelectedKeys(treeData, newSelected);
      const selectedByLevel = groupSelectedKeysByLevel(treeData, groupedKeys);

      setStore({
        policyStatusesAnd: [
          {
            statuses: selectedByLevel[0] as AggPolicyStatus[],
            codes: selectedByLevel[1],
          },
        ],
      });
    },
    [setStore, treeData]
  );
  const setSelectedNone = useCallback(() => setStore({policyStatusesAnd: undefined}), [setStore]);

  return {
    treeData,
    ...useOptimizedSelection({
      selected,
      setSelected,
      setSelectedNone,
      treeData,
      groupSelection: true,
    }),
    selectedNoneData: {
      policyStatusesAnd: undefined,
    },
    selectionCount: selected.length
      ? (policyStatusesAnd?.[0]?.statuses?.length || 0) + (policyStatusesAnd?.[0]?.codes?.length || 0)
      : 'All',
    loading,
  };
};
